/* eslint-disable max-len */
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { message, Button, Table, Space } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type AudiFinancingModuleSpecsFragment } from '../../../../../api/fragments';
import { useUpdateAudiFinancingModuleSalesPersonCodeMappingsMutation } from '../../../../../api/mutations/updateAudiFinancingModuleSalesPersonCodeMappings';
import ArrayField from '../../../../../components/fields/ArrayField';
import Form from '../../../../../components/fields/Form';
import FormItem from '../../../../../components/fields/FormItem';
import InputField from '../../../../../components/fields/InputField';
import SelectField, { type SelectFieldProps } from '../../../../../components/fields/SelectField';
import useHandleError from '../../../../../utilities/useHandleError';
import useValidator from '../../../../../utilities/useValidator';
import validators from '../../../../../utilities/validators';
import { useAudiFinancingModulePageHelpers } from '../HelpersContext';

type UpdateSalesPersonCodeMappingFormValues = { mappings: AudiFinancingModuleSpecsFragment['salesPersonCodeMappings'] };

const FormFields = ({ bankOptions }: { bankOptions: SelectFieldProps['options'] }) => {
    const { t } = useTranslation('audiFinancingModuleDetails');

    const {
        values: { mappings },
        setFieldValue,
    } = useFormikContext<UpdateSalesPersonCodeMappingFormValues>();

    const { setOnImported } = useAudiFinancingModulePageHelpers();

    useEffect(() => {
        setOnImported(mappings => {
            setFieldValue('mappings', mappings);
        });
    }, [setOnImported, setFieldValue]);

    return (
        <ArrayField
            name="mappings"
            render={({ push, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Table dataSource={mappings} pagination={false} rowKey={(record, index) => index.toString()}>
                        <Table.Column
                            dataIndex="salesPersonCode"
                            render={(_, __, rowIndex) => <InputField name={`mappings[${rowIndex}].salesPersonCode`} />}
                            title={
                                <FormItem
                                    label={t('audiFinancingModuleDetails:salesPersonCodeMappings.fields.sdmCode.label')}
                                    required
                                />
                            }
                        />
                        <Table.Column
                            dataIndex="bankIds"
                            render={(_, __, rowIndex) => (
                                <SelectField
                                    mode="multiple"
                                    name={`mappings[${rowIndex}].bankIds`}
                                    options={bankOptions}
                                    showSearch
                                />
                            )}
                            title={
                                <FormItem
                                    label={t('audiFinancingModuleDetails:salesPersonCodeMappings.fields.bankIds.label')}
                                />
                            }
                            width={400}
                        />
                        <Table.Column
                            dataIndex="bankCode"
                            render={(_, __, rowIndex) => <InputField name={`mappings[${rowIndex}].bankCode`} />}
                            title={
                                <FormItem
                                    label={t(
                                        'audiFinancingModuleDetails:salesPersonCodeMappings.fields.bankCode.label'
                                    )}
                                    required
                                />
                            }
                        />
                        <Table.Column
                            render={(_, __, rowIndex) => (
                                <Button
                                    icon={<DeleteOutlined />}
                                    onClick={() => remove(rowIndex)}
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                    type="link"
                                    danger
                                />
                            )}
                            width={60}
                        />
                    </Table>
                    <Button
                        icon={<PlusOutlined />}
                        onClick={() => push({ salesPersonCode: '', bankIds: [], bankCode: '' })}
                        type="primary"
                    >
                        {t('audiFinancingModuleDetails:salesPersonCodeMappings.labels.addMapping')}
                    </Button>
                </Space>
            )}
        />
    );
};

type UpdateSalesPersonCodeMappingFormProps = {
    module: AudiFinancingModuleSpecsFragment;
};

const LOADING_MSG_KEY = 'UpdateSalesPersonCodeMappingFormLoading';
const SUCCESS_MSG_KEY = 'UpdateSalesPersonCodeMappingFormSuccess';

const validations = validators.forEach(
    'mappings',
    validators.compose(
        validators.requiredString('salesPersonCode'),
        validators.requiredValue('bankIds'),
        validators.requiredString('bankCode')
    )
);

const UpdateSalesPersonCodeMappingForm = ({ module }: UpdateSalesPersonCodeMappingFormProps) => {
    const { t } = useTranslation('audiFinancingModuleDetails');

    const bankOptions = useMemo(
        () => module.banks.map(({ displayName, id }) => ({ label: displayName, value: id })),
        [module.banks]
    );
    const initialValues = useMemo<UpdateSalesPersonCodeMappingFormValues>(
        () => ({ mappings: module.salesPersonCodeMappings ?? [] }),
        [module?.salesPersonCodeMappings]
    );

    const [update] = useUpdateAudiFinancingModuleSalesPersonCodeMappingsMutation();
    const onSubmit = useHandleError<UpdateSalesPersonCodeMappingFormValues>(
        async values => {
            message.loading({
                content: t('audiFinancingModuleDetails:messages.updatingSalesPersonCodeMappings'),
                key: LOADING_MSG_KEY,
                duration: 0,
            });

            const { mappings } = values;
            await update({ variables: { moduleId: module.id, mappings } }).finally(() => {
                message.destroy(LOADING_MSG_KEY);
            });

            message.success({
                content: t('audiFinancingModuleDetails:messages.updateSuccess'),
                key: SUCCESS_MSG_KEY,
            });
        },
        [module.id, t, update]
    );
    const validate = useValidator(validations);

    return (
        <Formik<UpdateSalesPersonCodeMappingFormValues>
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={validate}
        >
            {({ handleSubmit }) => (
                <Form
                    id="updateSalesPersonCodeMappingForm"
                    name="updateSalesPersonCodeMappingForm"
                    onSubmitCapture={handleSubmit}
                    style={{ marginTop: -16 }}
                >
                    <FormFields bankOptions={bankOptions} />
                </Form>
            )}
        </Formik>
    );
};

export default UpdateSalesPersonCodeMappingForm;
